import { useFeatureValue } from '@growthbook/growthbook-react';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import useTimer from 'src/common/hooks/useTimer';
import { FlexibleStepperContextProps } from 'src/components/FlexibleStepper/types';
import { useFlexibleStepper } from 'src/components/FlexibleStepper/useFlexibleStepper';
import { NODE_ENV } from 'src/config';
import AuthStepperFooter from 'src/features/auth/presentation/components/AuthStepperFooter/AuthStepperFooter';
import AnimatedStepWrapper from 'src/features/auth/presentation/components/SignUpStepper/AnimatedStepWrapper';
import EmailInputStep from 'src/features/auth/presentation/components/SignUpStepper/EmailInputStep';
import VerificationOtpInput from 'src/features/auth/presentation/components/VerificationStepper/VerificationOtpInput';
import useAuthMutations from 'src/features/auth/presentation/hooks/useAuthMutations';
import useOtpVerification from 'src/features/verification/presentation/hooks/useOtpVerification';
import { FEATURE_FLAGS } from 'src/utils/growthBook/growthBook.constants';

interface EmailVerificationStepProps {
  onSuccess?: (stepperContext: FlexibleStepperContextProps) => void;
}

export default function EmailVerificationStep({
  onSuccess,
}: EmailVerificationStepProps) {
  const [hasSentCode, setHasSentCode] = useState(false);
  const { values } = useFormikContext<{ email: string }>();
  const supportHref = useFeatureValue(FEATURE_FLAGS.SUPPORT_LINK, '');

  const { sendEmailVerificationCodeMutation, verifyEmailCodeMutation } =
    useOtpVerification();
  const { logoutMutation } = useAuthMutations();

  const stepperContext = useFlexibleStepper();

  const { time, resetTimer, formattedTimer } = useTimer({
    enabled: hasSentCode,
    initialTime: NODE_ENV === 'development' ? 3000 : 30000,
  });

  const enableRequestNewCode =
    (!time || time === 0) && !sendEmailVerificationCodeMutation.isPending;

  const handleSendOtp = () => {
    if (enableRequestNewCode) {
      sendEmailVerificationCodeMutation.mutate(
        {
          to: values.email,
        },
        {
          onSuccess: () => {
            setHasSentCode(true);
            resetTimer();
          },
        }
      );
    }
  };

  const handleResendCode = () => {
    handleSendOtp();
  };

  const handleVerifyOtp = (otp: string) => {
    verifyEmailCodeMutation.mutate(
      {
        to: values.email,
        verificationCode: otp,
      },
      {
        onSuccess: () => {
          onSuccess?.(stepperContext);
        },
      }
    );
  };

  const isSendingCode = sendEmailVerificationCodeMutation.isPending;
  const enableLogout = stepperContext.isFirstStep && !hasSentCode;
  const backButtonLabel = !enableLogout ? 'Regresar' : 'Cerrar sesión';

  const handleLogout = async () => {
    logoutMutation.mutate();
  };

  const handleBack = () => {
    if (enableLogout) {
      return handleLogout();
    }

    if (hasSentCode) {
      return setHasSentCode(false);
    }

    stepperContext.moveBackward();
  };

  const handleSupportClick = () => {
    window.open(supportHref, '_blank', 'noopener,noreferrer');
  };

  return (
    <AnimatedStepWrapper>
      {hasSentCode ? (
        <VerificationOtpInput
          onSubmit={handleVerifyOtp}
          onResendCode={handleResendCode}
          enableRequestNewCode={enableRequestNewCode}
          timeToResend={formattedTimer}
          isError={verifyEmailCodeMutation.isError}
          isPending={verifyEmailCodeMutation.isPending}
          to={values.email}
        />
      ) : (
        <EmailInputStep
          onSubmit={() => handleSendOtp()}
          title={'Necesitamos verificar tu correo'}
          description={
            'Enviaremos un código de seis dígitos a tu correo. Si cambias la dirección de correo electrónico, deberas iniciar sesión con la nueva que ingresaste.'
          }
          submitLabel={
            isSendingCode
              ? 'Enviando...'
              : `Enviar código ${
                  enableRequestNewCode ? '' : `(${formattedTimer})`
                }`
          }
          disableNext={isSendingCode || !enableRequestNewCode}
        />
      )}
      <AuthStepperFooter
        backButton={{
          color: 'greyText',
          label: backButtonLabel,
          onClick: handleBack,
          variant: 'text',
        }}
        nextButton={{
          color: 'error',
          label: 'Ayuda',
          onClick: handleSupportClick,
          variant: 'text',
        }}
      />
    </AnimatedStepWrapper>
  );
}
